// src/App.js
import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import MobileInterface from './modules/MobileInterface';
import DashboardInterface from './modules/DashboardInterface';
import GestorDashboardInterface from './modules/GestorDashboardInterface';
import DashboardLogin from './components/DashboardLogin';
import { TermosDeUso, PoliticaDePrivacidade } from './components/LegalPages';
import { ExcluirConta, PoliticaDeReembolso } from './components/PaginasAjuda';
import { auth, db, gerarLinkDashboard } from './services/firebase';
import { onAuthStateChanged, signOut, signInAnonymously, signInWithCustomToken } from 'firebase/auth';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

// ===================================================================
// 🔗 GERAÇÃO DO LINK DE PAREAMENTO (celular → computador) — a
// autenticação de verdade acontece na Cloud Function
// gerarTokenPareamento, chamada dentro de TelaPareamento (App.js).
// ===================================================================

// O plano em cache no localStorage é deste APARELHO, não de uma conta. Se o
// dono anterior do cache for outra conta, descarta o plano velho antes de
// gravar o desta (senão o plano de uma conta de teste vazava pra outra).
function marcarDonoDoCachePlano(uid) {
  if (localStorage.getItem('phoenix_plan_owner_uid') !== uid) {
    localStorage.removeItem('phoenix_gestor_plan');
    localStorage.removeItem('phoenix_consultor_plan');
    localStorage.setItem('phoenix_plan_owner_uid', uid);
  }
}

// Busca o role E o plano salvos no Firestore para um usuário já autenticado
async function buscarRole(uid) {
  try {
    const userDoc = await getDoc(doc(db, 'usuarios', uid));
    if (userDoc.exists()) {
      const d = userDoc.data();
      // 🔥 Restaura o plano da nuvem para o localStorage ANTES do MobileInterface
      // ler — assim funciona mesmo em outro navegador/dispositivo, não só
      // no aparelho onde o plano foi comprado.
      // Marca de quem é esse cache de plano (ver MobileInterface: plano
      // em cache de OUTRA conta não pode vazar pra esta).
      marcarDonoDoCachePlano(uid);
      if (d.gestorPlan) localStorage.setItem('phoenix_gestor_plan', d.gestorPlan);
      if (d.consultorPlan) localStorage.setItem('phoenix_consultor_plan', d.consultorPlan);
      return String(d.role || d.userType || 'gestor').toLowerCase();
    }
  } catch (e) {
    // 🔥 CORRIGIDO — CAUSA RAIZ DO "MISSING OR INSUFFICIENT PERMISSIONS":
    // isso é um atraso raro, mas conhecido, do Firebase — pode levar uma
    // fração de segundo a mais pro token de login novo "destravar" de vez
    // no canal de conexão com o banco, mesmo já validado. Se acontecer,
    // esperamos 1 segundo (tempo de sobra) e tentamos mais uma vez —
    // dessa vez já deve estar tudo certo.
    if (e?.code === 'permission-denied') {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      try {
        const userDoc = await getDoc(doc(db, 'usuarios', uid));
        if (userDoc.exists()) {
          const d = userDoc.data();
          marcarDonoDoCachePlano(uid);
          if (d.gestorPlan) localStorage.setItem('phoenix_gestor_plan', d.gestorPlan);
          if (d.consultorPlan) localStorage.setItem('phoenix_consultor_plan', d.consultorPlan);
          return String(d.role || d.userType || 'gestor').toLowerCase();
        }
        return 'gestor';
      } catch (e2) {
        console.error('[PHOENIX] Erro ao buscar role (na 2ª tentativa):', e2);
      }
    } else {
      console.error('[PHOENIX] Erro ao buscar role:', e);
    }
  }
  return 'gestor';
}

function AppRoutes() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // 🔥 CORREÇÃO DE SEGURANÇA: o Dashboard abre numa aba nova via window.open
  // — e o navegador copia automaticamente o sessionStorage da aba de origem
  // pra aba nova (comportamento padrão do HTML5, não bug do Firebase). Isso
  // fazia o Dashboard entrar sozinho, sem pedir senha, sempre que aberto a
  // partir de uma sessão já logada no app. Esse flag só é setado DENTRO do
  // DashboardLogin, então mesmo herdando a sessão do Firebase, o Dashboard
  // sempre exige login próprio antes de mostrar qualquer dado.
  const [dashboardAutenticado, setDashboardAutenticado] = useState(
    () => sessionStorage.getItem('phoenix_dashboard_auth') === 'true'
  );

  // 🔗 PAREAMENTO POR QR CODE — pra onde ir assim que a autenticação sem
  // senha (setUser + setDashboardAutenticado, feitos juntos abaixo) realmente
  // terminar de aplicar. Guardar o destino aqui em vez de chamar navigate()
  // direto na hora do clique evita a corrida que causava "pede senha só às
  // vezes": antes, a troca de tela podia acontecer ANTES do React terminar
  // de processar "já estou autenticado", numa ordem que variava a cada
  // teste. Agora, o useEffect logo abaixo só troca de tela DEPOIS de
  // confirmar, no próprio estado, que a autenticação já está valendo.
  const [destinoAposPareamento, setDestinoAposPareamento] = useState(null);

  useEffect(() => {
    if (destinoAposPareamento && user && dashboardAutenticado) {
      navigate(destinoAposPareamento, { replace: true });
      setDestinoAposPareamento(null);
    }
  }, [destinoAposPareamento, user, dashboardAutenticado, navigate]);

  // Verifica se há usuário logado ao iniciar (mesma sessão do Firebase serve
  // tanto para o App quanto para o Dashboard — o que muda é qual TELA de
  // login cada rota mostra quando não há ninguém autenticado).
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        // Conta anônima não tem "role" nenhum salvo ainda — pula a busca
        // (evita um getDoc que sempre voltaria vazio).
        if (!currentUser.isAnonymous) {
          const role = await buscarRole(currentUser.uid);
          setUserRole(role);
        }
        setLoading(false);
      } else {
        setUser(null);
        setUserRole(null);
        setLoading(false);
        // Sem ninguém logado: zera o registro de "última atividade" (um novo login nunca herda a hora antiga).
        try { localStorage.removeItem('phoenix_ultima_atividade'); } catch (e) { /* ok */ }
        // 🔥 LOGIN ANÔNIMO AUTOMÁTICO — só na rota do app (/mobile), nunca
        // no Dashboard (que sempre exige login de verdade). Isso elimina a
        // etapa de "criar conta" antes de preencher a empresa ou comprar:
        // a pessoa já tem uma identidade válida assim que abre o app, sem
        // nenhuma tela. Se ela quiser, pode "promover" essa conta anônima
        // pra uma de verdade depois (com e-mail/senha), sem perder nada
        // do que já fez — é só um convite no final, nunca uma barreira.
        //
        // 🔥 CORRIGIDO — CAUSA RAIZ DO "PEDE SENHA SÓ ÀS VEZES NO GESTOR":
        // esse `location.pathname` aqui é uma FOTO antiga, tirada no
        // instante em que este efeito foi criado — nunca mais atualizada,
        // mesmo que a pessoa já tenha saído do app. Sequência real do bug:
        // 1) pessoa abre o app (/mobile), o Firebase ainda não tinha
        //    ninguém logado, então esse login anônimo começa a ser pedido;
        // 2) antes dele terminar (rede um pouco lenta), a pessoa já saiu
        //    pra escanear o QR Code e logou de VERDADE em /parear;
        // 3) o login anônimo "atrasado" finalmente termina — e como ele
        //    ainda pensava que a foto antiga ("estou em /mobile") valia,
        //    ele SOBRESCREVE o login de verdade que acabou de acontecer.
        // Trocando pela URL de verdade (window.location.pathname, lida na
        // hora), ele agora percebe que a pessoa já saiu do app e desiste
        // sozinho, sem atrapalhar o login de verdade.
        if (window.location.pathname.startsWith('/mobile') && !auth.currentUser) {
          try {
            await signInAnonymously(auth);
          } catch (erro) {
            console.error('[PHOENIX] Erro ao criar sessão anônima:', erro);
          }
        }
      }
    });
    return () => unsubscribe();
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.error("Erro ao fazer logout:", e);
    } finally {
      // 🔒 Força a atualização do state mesmo que onAuthStateChanged não dispare
      // (pode acontecer se a sessão já tiver expirado silenciosamente antes do clique,
      // por conta da browserSessionPersistence — signOut() não gera erro nesse caso,
      // mas também não dispara um evento de mudança real).
      setUser(null);
      setUserRole(null);
      setDashboardAutenticado(false);
      sessionStorage.removeItem('phoenix_dashboard_auth');

      localStorage.removeItem('phoenix_user_type');
      localStorage.removeItem('phoenix_active_plan');
      // 🔥 NÃO apagamos mais phoenix_gestor_plan / phoenix_consultor_plan aqui.
      // Esses dois representam a assinatura paga do usuário — apagar no logout
      // fazia o plano sumir para sempre, pois ele nunca era salvo em outro lugar
      // (Firestore). O useEffect [user] no MobileInterface.jsx já cuida de
      // restaurar o perfil certo (Gestor/Consultor) a partir do que estiver salvo.
      localStorage.removeItem('phoenix_user_level');
      localStorage.removeItem('phoenix_user_name');
      localStorage.removeItem('phoenix_user_email');
      localStorage.removeItem('phoenix_gestor_email');
      localStorage.removeItem('phoenix_consultor_email');
      localStorage.removeItem('phoenix_history_consultor');
      localStorage.removeItem('phoenix_history_gestor');

      // 🔥 CORRIGIDO: antes, QUALQUER logout (incluindo o automático por
      // inatividade) mandava a pessoa direto pra '/mobile' — não importava
      // se ela estava trabalhando no Dashboard Consultor ou Gestor. Alguém
      // usando o Dashboard no computador, ao ser desconectado por
      // inatividade, caía de repente na tela do app de celular, sem
      // nenhuma explicação. Agora, se a pessoa já está num Dashboard, ela
      // simplesmente CONTINUA lá — a troca pra tela de login
      // (DashboardLogin) já acontece sozinha, porque 'user' virou null
      // logo acima. Só forçamos ida pro '/mobile' quando o logout
      // acontece fora dos Dashboards (ex: dentro do próprio app).
      const estaNumDashboard = location.pathname.startsWith('/dashboard-');
      if (!estaNumDashboard) {
        navigate('/mobile');
      }
    }
  };

  // 🔒 SEGURANÇA: expira a sessão após 10 minutos de inatividade (igual apps
  // bancários) — só pra contas de verdade. Sessão anônima não tem "dono"
  // nenhum pra proteger; desligar ela no meio do preenchimento só criava
  // uma sessão anônima NOVA (com ID diferente), órfã dos dados que a
  // pessoa já tinha começado a salvar (erro de permissão em cascata).
  useEffect(() => {
    if (!user || user.isAnonymous) return;
    const TEMPO_LIMITE_MS = 10 * 60 * 1000; // 10 minutos
    const CHAVE = 'phoenix_ultima_atividade';
    let timeoutId;
    let ultimaGravacao = 0;

    // 🔒 O cronômetro em memória NÃO basta no app: quando o Android congela ou mata o processo em segundo
    // plano (ou o aparelho dorme), o setTimeout não dispara e a sessão (que persiste no aparelho) continuava
    // aberta por horas. Por isso a última atividade também é gravada com a hora real (e o uid) e conferida
    // sempre que o app volta pra frente e a cada 30 s: passou de 10 min parado → sai.
    const lerUltima = () => {
      try {
        const v = JSON.parse(localStorage.getItem(CHAVE) || 'null');
        return v && v.uid === user.uid ? Number(v.t) : null;
      } catch (e) { return null; }
    };
    const gravarAtividade = (forcar) => {
      const agora = Date.now();
      if (!forcar && agora - ultimaGravacao < 15000) return; // não escreve a cada toque
      ultimaGravacao = agora;
      try { localStorage.setItem(CHAVE, JSON.stringify({ uid: user.uid, t: agora })); } catch (e) { /* sem storage: segue só com o timer */ }
    };
    const expirou = () => {
      const t = lerUltima();
      return t !== null && Date.now() - t > TEMPO_LIMITE_MS;
    };
    const sair = () => {
      try { localStorage.removeItem(CHAVE); } catch (e) { /* ok */ }
      handleLogout();
    };

    const resetarTimer = () => {
      gravarAtividade(false);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(sair, TEMPO_LIMITE_MS);
    };
    const conferir = () => {
      if (document.visibilityState === 'hidden') return;
      if (expirou()) sair();
    };

    // Reabriu o app (processo foi morto) e já passou do limite → sai antes de mostrar qualquer coisa.
    if (expirou()) { sair(); return; }

    const eventos = ['mousedown', 'keydown', 'touchstart', 'pointerdown'];
    eventos.forEach((evento) => window.addEventListener(evento, resetarTimer, { passive: true }));
    // scroll não "borbulha": captura também a rolagem dentro das telas internas do app
    window.addEventListener('scroll', resetarTimer, { passive: true, capture: true });
    document.addEventListener('visibilitychange', conferir);
    window.addEventListener('focus', conferir);
    const intervaloId = setInterval(conferir, 30000);
    gravarAtividade(true);
    resetarTimer();

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervaloId);
      eventos.forEach((evento) => window.removeEventListener(evento, resetarTimer));
      window.removeEventListener('scroll', resetarTimer, true);
      document.removeEventListener('visibilitychange', conferir);
      window.removeEventListener('focus', conferir);
    };
    // 🔥 CORRIGIDO: antes dependia de [user] (o objeto inteiro). O
    // Firebase Auth às vezes recria esse objeto por dentro — durante
    // sincronização entre abas, renovação de token em segundo plano —
    // mesmo continuando sendo a MESMA pessoa logada. Cada recriação
    // fazia o React achar que "o usuário mudou", desmontando e remontando
    // esse efeito, o que reiniciava o cronômetro do zero. Resultado: o
    // timer nunca completava os 10 minutos, porque vivia sendo reiniciado
    // sozinho — sem a pessoa tocar em nada. Agora depende só do UID (uma
    // string, que não muda enquanto for a mesma pessoa) e da flag
    // isAnonymous — nunca reinicia à toa.
  }, [user?.uid, user?.isAnonymous]);

  if (loading) {
    return (
      <div className="h-screen w-screen bg-[#010208] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-lime-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // 🔥 CORREÇÃO: onSwitchMode nunca era passado pros Dashboards — os botões
  // "App Móvel", "Assinar Protocolo" e "Comprar Avulso" não faziam nada.
  // Por enquanto leva sempre pro app (/mobile); deep-link direto pra tela
  // de upgrade ou de compra avulsa fica pra quando o checkout web entrar.
  const onSwitchMode = () => navigate('/mobile');

  return (
    <Routes>
      {/* 🔒 PRODUÇÃO: a raiz do site vai direto pro app — nunca mostra a
          tela de escolha de dev pra usuário real. Durante testes, acesse
          /dashboard-gestor ou /dashboard-consultor direto pela URL. */}
      <Route path="/" element={<Navigate to="/mobile" replace />} />

      {/* ===================================================================
          MUNDO 1: APP (celular). Login embutido na própria tela inicial
          do MobileInterface — nunca redireciona para fora dessa rota.
          =================================================================== */}
      <Route
        path="/mobile"
        element={
          <div className="relative h-screen w-screen bg-black">
            <MobileInterface
              user={user}
              userPlan={userRole === 'gestor' ? localStorage.getItem('phoenix_active_plan') : 'entry'}
              onLoginSuccess={async (loggedUser) => {
                setUser(loggedUser);
                const role = await buscarRole(loggedUser.uid);
                setUserRole(role);
              }}
              onLogout={handleLogout}
            />
          </div>
        }
      />

      {/* ===================================================================
          MUNDO 2: DASHBOARD (navegador/desktop). Login próprio (DashboardLogin),
          nunca depende do App nem redireciona para /mobile.
          =================================================================== */}
      <Route
        path="/dashboard-gestor"
        element={
          !user || !dashboardAutenticado ? (
            <DashboardLogin tipo="gestor" onLoginSuccess={(userData) => {
              sessionStorage.setItem('phoenix_dashboard_auth', 'true');
              setDashboardAutenticado(true);
              setUser(userData.user); setUserRole(userData.role);
            }} />
          ) : (
            <GestorDashboardInterface onLogout={handleLogout} onSwitchMode={onSwitchMode} />
          )
        }
      />

      <Route
        path="/dashboard-consultor"
        element={
          !user || !dashboardAutenticado ? (
            <DashboardLogin tipo="consultor" onLoginSuccess={(userData) => {
              sessionStorage.setItem('phoenix_dashboard_auth', 'true');
              setDashboardAutenticado(true);
              setUser(userData.user); setUserRole(userData.role);
            }} />
          ) : (
            <DashboardInterface
              onLogout={handleLogout}
              onSwitchMode={onSwitchMode}
              isConsultor={true}
              userEmail={user?.email}
            />
          )
        }
      />

      {/* PAREAMENTO — troca o token gerado no celular por uma sessão real neste navegador */}
      <Route
        path="/parear"
        element={
          <TelaPareamento
            onPareamentoConcluido={(userData) => {
              // 🔥 CORRIGIDO: sem isso, o pareamento fazia login de verdade
              // mas caía em '/' (que vai pro app) e, mesmo indo direto pro
              // Dashboard, pedia senha de novo — porque 'dashboardAutenticado'
              // só era preenchido por dentro do DashboardLogin. Aqui fazemos
              // exatamente o que o DashboardLogin faz ao logar com sucesso.
              // 🔥 CORRIGIDO (2ª rodada): não chamamos navigate() aqui na
              // hora — só guardamos o destino. Quem navega de verdade é o
              // useEffect lá em cima, e só depois que 'user' e
              // 'dashboardAutenticado' já estiverem realmente atualizados.
              sessionStorage.setItem('phoenix_dashboard_auth', 'true');
              setDashboardAutenticado(true);
              setUser(userData.user);
              setUserRole(userData.role);
              setDestinoAposPareamento(userData.role === 'consultor' ? '/dashboard-consultor' : '/dashboard-gestor');
            }}
          />
        }
      />

      {/* Páginas públicas (Termos e Privacidade) — exigidas pelo Asaas, pela
          Google Play e pela LGPD. Antes o texto existia em LegalPages.jsx mas
          nenhuma rota o servia: /termos-de-uso caía no "*" e voltava pro app. */}
      <Route path="/termos-de-uso" element={<TermosDeUso />} />
      <Route path="/politica-de-privacidade" element={<PoliticaDePrivacidade />} />
      {/* Páginas de ajuda: reembolso (7 dias, confirmado pelo advogado em 13/10/2026) e exclusão de conta (exigida pelas lojas). */}
      <Route path="/reembolso" element={<PoliticaDeReembolso />} />
      <Route path="/excluir-conta" element={<ExcluirConta />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// ===================================================================
// 🔗 TELA DE PAREAMENTO
// ===================================================================
function TelaPareamento({ onPareamentoConcluido }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('validando');
  const [opcoesDashboard, setOpcoesDashboard] = useState(null); // {user} — preenchido só quando tem os 2 perfis
  const [erroPareamento, setErroPareamento] = useState(null);
  // 🔥 CORRIGIDO — CAUSA RAIZ DO "LINK EXPIRADO" LOGO APÓS FUNCIONAR: o
  // React (em modo de desenvolvimento, de propósito) roda esse efeito DUAS
  // VEZES seguidas pra ajudar a pegar bugs. Como o token do QR Code só pode
  // ser usado uma vez (é assim que garantimos que ninguém mais reaproveite
  // o mesmo QR Code depois), a 1ª chamada validava e "gastava" o token — a
  // 2ª vinha logo atrás, achava ele já usado, e sobrescrevia a tela com
  // "expirado" (às vezes depois de já ter redirecionado, às vezes travando
  // ali mesmo). Essa trava garante que só a PRIMEIRA chamada de fato rode.
  const jaValidouRef = useRef(false);

  useEffect(() => {
    const validarToken = async () => {
      if (jaValidouRef.current) return;
      jaValidouRef.current = true;
      const token = searchParams.get('token');
      if (!token) {
        setStatus('erro');
        return;
      }
      try {
        // A Cloud Function já confere tudo sozinha (existe, não expirou,
        // não foi usado) — não precisa checar antes aqui no navegador.
        // Isso também evita precisar liberar leitura pública da coleção
        // linkTokens nas Regras do Firestore, já que quem escaneia o QR
        // Code ainda não está logado nesse navegador.
        const functions = getFunctions(auth.app, 'southamerica-east1');
        const gerarToken = httpsCallable(functions, 'gerarTokenPareamento');
        const resultado = await gerarToken({ token });
        const credencial = await signInWithCustomToken(auth, resultado.data.customToken);
        // 🔥 CORRIGIDO — CAUSA RAIZ DO "MISSING OR INSUFFICIENT PERMISSIONS":
        // problema bem conhecido do Firebase — logo depois de logar com um
        // token especial (como o do QR Code), o "crachá de acesso" interno
        // que prova quem você é pro banco de dados pode levar um instante
        // pra se atualizar de verdade. Se alguma leitura acontece nesse
        // instante (como as que o Dashboard do Gestor faz, várias de uma
        // vez, assim que abre), o banco nega o acesso achando que ainda é
        // a sessão antiga. Forçando a renovação aqui, ANTES de navegar pra
        // qualquer Dashboard, garantimos que o crachá já está 100% válido
        // quando essas leituras acontecerem.
        await credencial.user.getIdToken(true);

        // 🔥 CORRIGIDO: antes usava só o campo 'role' da conta (definido lá
        // na criação, geralmente 'gestor') pra decidir o destino — então
        // quem tinha os DOIS perfis ativos (comprou Gestor e Consultor)
        // sempre caía no Gestor, sem jeito nenhum de chegar no Consultor
        // por aqui. Agora checamos os planos de verdade: se só um estiver
        // ativo, vai direto pra ele; se os dois estiverem ativos, pergunta.
        const userDoc = await getDoc(doc(db, 'usuarios', credencial.user.uid));
        const dados = userDoc.exists() ? userDoc.data() : {};
        // 🔥 CORRIGIDO: o webhook do Asaas grava o plano do Gestor no campo
        // 'plan' (não 'gestorPlan' — esse é usado só por migração de contas
        // antigas). Checando só 'gestorPlan', a maioria das contas de Gestor
        // de verdade (liberadas pelo webhook) nunca era reconhecida aqui —
        // por isso a tela de escolha não aparecia e caía direto no Consultor,
        // ou, quando só tinha Gestor mesmo, o app tratava como "sem plano
        // nenhum" e às vezes derrubava o fluxo de pareamento no meio.
        const planoGestor = dados.plan || dados.gestorPlan;
        const temGestor = planoGestor && planoGestor !== 'entry';
        const temConsultor = dados.consultorPlan && dados.consultorPlan !== 'free';

        if (temGestor && temConsultor) {
          setOpcoesDashboard({ user: credencial.user });
          setStatus('escolher');
          return;
        }

        const role = temConsultor ? 'consultor' : 'gestor';
        setStatus('sucesso');
        // 🔥 CORRIGIDO — CAUSA RAIZ DO "ÀS VEZES PEDE SENHA": antes,
        // navegávamos pro Dashboard direto daqui, com um tempo fixo de
        // espera "no chute" (1200ms) pra torcer que o App.js já tivesse
        // processado a autenticação. Só que isso é uma corrida: às vezes o
        // tempo era suficiente, às vezes não (dependia da velocidade do
        // computador, do Firestore, etc.) — por isso funcionava num teste e
        // falhava no outro, sem padrão. Agora só avisamos o App.js que
        // terminou; quem decide a HORA CERTA de trocar de tela é o
        // useEffect lá, que só age depois de confirmar (no próprio estado)
        // que a autenticação já está valendo de verdade — sem chute.
        if (typeof onPareamentoConcluido === 'function') {
          onPareamentoConcluido({ user: credencial.user, role });
        }
      } catch (e) {
        console.error('[PHOENIX] Erro ao validar token de pareamento:', e);
        setErroPareamento(e?.code === 'functions/failed-precondition' ? 'usado' : null);
        setStatus('erro');
      }
    };
    validarToken();
  }, [searchParams, onPareamentoConcluido]);

  const escolherDashboard = (role) => {
    if (!opcoesDashboard) return;
    setStatus('sucesso');
    if (typeof onPareamentoConcluido === 'function') {
      onPareamentoConcluido({ user: opcoesDashboard.user, role });
    }
    // 🔥 CORRIGIDO: essa função não navega mais sozinha. Antes ela media um
    // tempo "no chute" (300ms) antes de trocar de tela — funcionava na
    // maioria das vezes, mas não sempre (por isso o bug parecia aleatório).
    // Agora é só avisar o App.js (acima) que a escolha foi feita; ele troca
    // de tela sozinho, na hora certa, confirmada pelo próprio estado.
  };

  return (
    <div className="h-screen w-screen bg-[#010208] flex flex-col items-center justify-center text-center px-6">
      {status === 'validando' && (
        <>
          <div className="w-12 h-12 border-4 border-lime-400 border-t-transparent rounded-full animate-spin mb-6"></div>
          <p className="text-white font-black uppercase tracking-widest text-sm">Conectando ao seu painel...</p>
        </>
      )}
      {status === 'escolher' && (
        <>
          <p className="text-white font-black uppercase tracking-widest text-lg mb-6">Qual painel você quer abrir?</p>
          <div className="flex flex-col gap-3 w-full max-w-xs">
            <button
              onClick={() => escolherDashboard('gestor')}
              className="bg-lime-400 text-black font-black uppercase text-xs tracking-widest px-6 py-4 rounded-full hover:bg-lime-300 transition-all"
            >
              Dashboard Gestor
            </button>
            <button
              onClick={() => escolherDashboard('consultor')}
              className="bg-white/10 border border-white/20 text-white font-black uppercase text-xs tracking-widest px-6 py-4 rounded-full hover:bg-white/20 transition-all"
            >
              Dashboard Consultor
            </button>
          </div>
        </>
      )}
      {status === 'sucesso' && (
        <>
          <p className="text-lime-400 font-black uppercase tracking-widest text-lg mb-2">Conectado!</p>
          <p className="text-slate-500 text-sm">Redirecionando...</p>
        </>
      )}
      {status === 'erro' && (
        <>
          <p className="text-red-400 font-black uppercase tracking-widest text-lg mb-2">Link inválido ou expirado</p>
          <p className="text-slate-500 text-sm mb-6">
            {erroPareamento === 'usado'
              ? 'Esse QR Code já foi usado. Gere um novo no aplicativo do seu celular.'
              : 'Gere um novo link no aplicativo do seu celular.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-white/10 border border-white/10 hover:bg-white/20 text-white text-[10px] font-black uppercase tracking-widest px-6 py-3 rounded-full transition-all"
          >
            Voltar
          </button>
        </>
      )}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}